import { useCallback, useMemo } from "react";

import dayjs from "dayjs";

import { useTranslate } from "@similarweb/i18n-provider";
import { ErrorBox } from "@similarweb/icons";
import { colorsPalettes } from "@similarweb/styles";
import { Pagination, PaginationInput, PixelPlaceholderLoader } from "@similarweb/ui-components";
import { Flex, Text } from "@similarweb/ui-components/foundations";

import { abbrNumberFilter } from "app/filters/ngFilters";

import { HeatMap } from "../../shared/heatmap/HeatMap";
import type { IHeatMapEventsResult } from "../../shared/heatmap/heatmap-events";
import { DEFAULT_COLORS, buildGetColor, type ColorScale, type GetCellColor } from "../../shared/heatmap/heatmap.shared";
import { LegendContainer } from "../../shared/heatmap/HeatMap.styles";

import { renderPublisherLabel } from "./PublisherLabel";
import {
    PUBLISHERS_PER_PAGE,
    type VerificationRecord,
    getBucketUnitRange,
    getScreenshotsCount,
    parsePublisherLabel,
} from "./verification-widget.utils";
import { VerificationTooltip } from "./VerificationTooltip";
import { HeatMapLoaderWrapper, VerificationHeatMapWrapper } from "./VerificationWidget.styles";

type HeatMapResult = IHeatMapEventsResult<VerificationRecord>;

const formatAbbr = abbrNumberFilter();

const DATA_AVAILABLE_FROM = "2024-12-01";

// Linear thresholds across [0, 1] for the 6 value colors of DEFAULT_COLORS.
const RATIO_VALUE_BUCKETS = DEFAULT_COLORS.length - 1;
const RATIO_COLOR_SCALE: ColorScale = {
    colors: DEFAULT_COLORS,
    thresholds: Array.from({ length: RATIO_VALUE_BUCKETS }, (_, i) => (i + 1) / RATIO_VALUE_BUCKETS),
};
const getColorByRatio = buildGetColor(RATIO_COLOR_SCALE);

const buildScreenshotsRatioGetColor = (rowTimesSeenTotals: Record<string, number>): GetCellColor => {
    return (value, rowKey) => {
        if (!rowKey || value <= 0) {
            return DEFAULT_COLORS[0];
        }
        const total = rowTimesSeenTotals[rowKey] ?? 0;
        if (total <= 0) {
            return DEFAULT_COLORS[0];
        }
        return getColorByRatio(Math.min(value / total, 1));
    };
};

interface IVerificationHeatMapViewProps {
    isFetching: boolean;
    data: HeatMapResult["data"];
    lookup: HeatMapResult["lookup"];
    labelEvery: HeatMapResult["labelEvery"];
    maxCellValue: number;
    page: number;
    publishersCount: number;
    dateFrom?: string;
    dateTo?: string;
    onlyScreenshots: boolean;
    rowTimesSeenTotals: Record<string, number> | null;
    onPageChange: (page: number) => void;
    onDetailsClick: (cell: { publisher: string; dateFrom?: string; dateTo?: string; expectedCount?: number }) => void;
}

export const VerificationHeatMapView = ({
    isFetching,
    data,
    lookup,
    labelEvery,
    maxCellValue,
    page,
    publishersCount,
    dateFrom,
    dateTo,
    onlyScreenshots,
    rowTimesSeenTotals,
    onPageChange,
    onDetailsClick,
}: IVerificationHeatMapViewProps) => {
    const { translate } = useTranslate();

    const selectedRange = useMemo(() => ({ from: dateFrom, to: dateTo }), [dateFrom, dateTo]);

    const yAxisConfig = useMemo(
        () => ({
            position: "left" as const,
            header: translate("ad.intelligence.verification.heatmap.publisher_axis"),
            renderLabel: renderPublisherLabel,
        }),
        [translate],
    );

    const handleCellClick = useCallback(
        (args: { rowKey: string; colKey: string; value: number }) => {
            const { rowKey, colKey, value } = args;
            const bucket = lookup(rowKey, colKey);
            const { name } = parsePublisherLabel(rowKey);
            const unitRange = bucket ? getBucketUnitRange(bucket, selectedRange) : undefined;
            onDetailsClick({
                publisher: name,
                dateFrom: unitRange?.dateFrom,
                dateTo: unitRange?.dateTo,
                expectedCount: value,
            });
        },
        [lookup, onDetailsClick, selectedRange],
    );

    const formatTooltip = useCallback(
        (args: { rowKey: string; colKey: string; value: number }) => {
            const { rowKey, colKey, value } = args;
            if (value === 0) {
                return null;
            }

            const bucket = lookup(rowKey, colKey);
            const { name } = parsePublisherLabel(rowKey);

            if (!bucket) {
                return translate("ad.intelligence.verification.tooltip.fallback", {
                    publisher: name,
                    period: colKey,
                    captures: formatAbbr(value),
                });
            }

            const unitRange = getBucketUnitRange(bucket, selectedRange);

            return (
                <VerificationTooltip
                    publisher={name}
                    dateFrom={unitRange.dateFrom}
                    dateTo={unitRange.dateTo}
                    screenshots={getScreenshotsCount(bucket.events)}
                    timesSeen={value}
                    onlyScreenshots={onlyScreenshots}
                    onDetailsClick={() =>
                        onDetailsClick({
                            publisher: name,
                            dateFrom: unitRange.dateFrom,
                            dateTo: unitRange.dateTo,
                            expectedCount: value,
                        })
                    }
                />
            );
        },
        [lookup, onDetailsClick, onlyScreenshots, selectedRange, translate],
    );

    const getColor = useMemo(
        () => (rowTimesSeenTotals ? buildScreenshotsRatioGetColor(rowTimesSeenTotals) : undefined),
        [rowTimesSeenTotals],
    );

    if (isFetching) {
        return (
            <HeatMapLoaderWrapper
                alignItems="center"
                justifyContent="center"
                width="100%"
            >
                <PixelPlaceholderLoader
                    width="100%"
                    height={240}
                />
            </HeatMapLoaderWrapper>
        );
    }

    return (
        <VerificationHeatMapWrapper>
            <HeatMap
                data={data}
                scaleStrategy="row"
                getColor={getColor}
            >
                <HeatMap.Grid
                    xAxis={{ labelEvery }}
                    yAxis={yAxisConfig}
                    formatTooltip={formatTooltip}
                    onCellClick={handleCellClick}
                />
                <Flex
                    justifyContent="space-between"
                    alignItems="center"
                >
                    <Flex
                        alignItems="center"
                        gap="16px"
                    >
                        <HeatMap.Legend
                            minLabel={
                                <Text
                                    fontVariant="caption"
                                    color={colorsPalettes.carbon[400]}
                                >
                                    {translate("ad.intelligence.verification.legend.no_data")}
                                </Text>
                            }
                            maxLabel={
                                <Text
                                    fontVariant="caption"
                                    color={colorsPalettes.carbon[400]}
                                >
                                    {translate("ad.intelligence.verification.legend.most_active")}
                                </Text>
                            }
                        />
                        {dateFrom && dayjs.utc(dateFrom).isBefore(DATA_AVAILABLE_FROM) ? (
                            <LegendContainer fontVariant="caption">
                                <Flex
                                    alignItems="center"
                                    gap="4px"
                                >
                                    <ErrorBox
                                        size={16}
                                        color={colorsPalettes.carbon[300]}
                                    />
                                    <Text
                                        fontVariant="caption"
                                        color={colorsPalettes.carbon[400]}
                                    >
                                        {translate("ad.intelligence.verification.footer.no_data_note")}
                                    </Text>
                                </Flex>
                            </LegendContainer>
                        ) : null}
                    </Flex>
                    <Pagination
                        page={page}
                        itemsCount={publishersCount}
                        itemsPerPage={PUBLISHERS_PER_PAGE}
                        handlePageChange={onPageChange}
                        captionPosition="center"
                        captionElement={PaginationInput}
                        hasItemsPerPageSelect={false}
                    />
                </Flex>
            </HeatMap>
        </VerificationHeatMapWrapper>
    );
};
